/**
 * AI Prompt Debug Drawer - Development-only debugging console
 * Shows complete conversation including system prompt and message history.
 * Activated via Ctrl+Shift+D keyboard shortcut.
 */
"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { X, Copy, Check, ChevronDown, ChevronRight, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";

type MessageRole = "system" | "user" | "assistant" | "tool";

export interface DebugMessage {
  id?: string;
  role: MessageRole;
  content: string;
  timestamp?: Date;
  metadata?: Record<string, unknown>;
}

export interface AiPromptDebugDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  systemPrompt?: string;
  messages: DebugMessage[];
  modelId?: string;
  structuredOutput?: Record<string, unknown> | null;
  debugInfo?: Record<string, unknown>;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-slate-700 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-400" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-slate-400" />
      )}
    </button>
  );
}

function ExpandableText({ text, maxLength = 100 }: { text: string; maxLength?: number }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const needsTruncation = text.length > maxLength;

  if (!needsTruncation) {
    return <span className="text-green-400">&quot;{text}&quot;</span>;
  }

  return (
    <span className="text-green-400">
      {isExpanded ? (
        <>
          &quot;{text}&quot;
          <button
            onClick={() => setIsExpanded(false)}
            className="ml-1 text-slate-400 hover:text-slate-200 text-xs"
            title="Collapse"
          >
            [collapse]
          </button>
        </>
      ) : (
        <>
          &quot;{text.substring(0, maxLength)}...&quot;
          <button
            onClick={() => setIsExpanded(true)}
            className="ml-1 inline-flex items-center gap-0.5 text-cyan-400 hover:text-cyan-300 text-xs"
            title="Show full text"
          >
            <Maximize2 className="h-3 w-3" />
            <span>{text.length} chars</span>
          </button>
        </>
      )}
    </span>
  );
}

function CollapsibleSection({
  title,
  badge,
  children,
  defaultOpen = true,
  badgeColor = "bg-slate-600",
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badgeColor?: string;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-slate-700 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-slate-800/50 transition-colors"
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400" />
        )}
        <span className="text-sm font-medium text-slate-200">{title}</span>
        {badge && (
          <span className={cn("text-xs px-1.5 py-0.5 rounded", badgeColor)}>
            {badge}
          </span>
        )}
      </button>
      {isOpen && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

function MessageBlock({ message, index }: { message: DebugMessage; index: number }) {
  const roleColors: Record<MessageRole, string> = {
    system: "bg-purple-600/20 text-purple-300 border-purple-500/30",
    user: "bg-blue-600/20 text-blue-300 border-blue-500/30",
    assistant: "bg-green-600/20 text-green-300 border-green-500/30",
    tool: "bg-yellow-600/20 text-yellow-300 border-yellow-500/30",
  };

  const roleBadgeColors: Record<MessageRole, string> = {
    system: "bg-purple-600",
    user: "bg-blue-600",
    assistant: "bg-green-600",
    tool: "bg-yellow-600",
  };

  return (
    <div className={cn("rounded-lg border p-3 mb-2", roleColors[message.role])}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-xs font-medium px-2 py-0.5 rounded uppercase",
              roleBadgeColors[message.role],
            )}
          >
            {message.role}
          </span>
          <span className="text-xs text-slate-500">#{index + 1}</span>
          {message.timestamp && (
            <span className="text-xs text-slate-500">
              {message.timestamp.toLocaleTimeString()}
            </span>
          )}
        </div>
        <CopyButton text={message.content} />
      </div>
      <pre className="text-xs whitespace-pre-wrap break-words font-mono leading-relaxed">
        {message.content}
      </pre>
      {message.metadata && Object.keys(message.metadata).length > 0 && (
        <details className="mt-2">
          <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400">
            Metadata
          </summary>
          <pre className="text-xs text-slate-400 mt-1 overflow-x-auto">
            {JSON.stringify(message.metadata, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function StructuredOutputSection({ output }: { output: Record<string, unknown> }) {
  // Helper to determine if a value is "filled" (non-null, non-empty)
  const isFilled = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
  };

  const renderValue = (value: unknown, key: string): ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-slate-500 italic">null</span>;
    }
    if (typeof value === "string") {
      return <ExpandableText text={value} />;
    }
    if (typeof value === "boolean") {
      return <span className="text-yellow-400">{value.toString()}</span>;
    }
    if (typeof value === "number") {
      return <span className="text-blue-400">{value}</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-slate-500">[]</span>;
      }
      return (
        <span className="text-slate-300">
          [{value.length} items]
        </span>
      );
    }
    if (typeof value === "object") {
      return renderObject(value as Record<string, unknown>, key);
    }
    return <span className="text-slate-300">{String(value)}</span>;
  };

  const renderObject = (obj: Record<string, unknown>, parentKey: string): ReactNode => {
    const entries = Object.entries(obj);
    const filledCount = entries.filter(([, v]) => isFilled(v)).length;

    return (
      <div className="ml-3 border-l border-slate-700 pl-3">
        <div className="text-xs text-slate-500 mb-1">
          {filledCount}/{entries.length} fields filled
        </div>
        {entries.map(([key, value]) => (
          <div key={`${parentKey}-${key}`} className="flex items-start gap-2 py-0.5">
            <span
              className={cn(
                "text-xs font-mono",
                isFilled(value) ? "text-cyan-400" : "text-slate-600",
              )}
            >
              {key}:
            </span>
            <span className="text-xs font-mono flex-1">{renderValue(value, `${parentKey}-${key}`)}</span>
          </div>
        ))}
      </div>
    );
  };

  const statusColors: Record<string, string> = {
    complete: "bg-green-600",
    partial: "bg-yellow-600",
    skipped: "bg-slate-600",
    limit_exceeded: "bg-red-600",
  };

  const status = output.status as string | undefined;

  // Extract sections (identity, journey, bigIdea) - excluding status and notes
  const sections = Object.entries(output)
    .filter(([key, value]) => key !== "status" && key !== "notes" && !!value && typeof value === "object")
    .map(([key, value]) => ({
      key,
      value: value as Record<string, unknown>,
    }));

  const notes = Array.isArray(output.notes) ? (output.notes as unknown[]) : [];

  return (
    <div className="space-y-3">
      {/* Status badge */}
      {status && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Status:</span>
          <span
            className={cn(
              "text-xs font-medium px-2 py-0.5 rounded uppercase",
              statusColors[status] || "bg-slate-600",
            )}
          >
            {status}
          </span>
        </div>
      )}

      {/* Main sections */}
      {sections.map(({ key, value: objValue }) => {
        const entries = Object.entries(objValue);
        const filledCount = entries.filter(([, v]) => isFilled(v)).length;

        return (
          <div key={key} className="rounded bg-slate-800/50 p-2">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold text-slate-200 uppercase">{key}</span>
              <span
                className={cn(
                  "text-xs px-1.5 py-0.5 rounded",
                  filledCount > 0 ? "bg-green-600/30 text-green-400" : "bg-slate-700 text-slate-500",
                )}
              >
                {filledCount}/{entries.length}
              </span>
            </div>
            {renderObject(objValue, key)}
          </div>
        );
      })}

      {/* Notes */}
      {notes.length > 0 && (
        <div className="rounded bg-slate-800/50 p-2">
          <span className="text-xs font-semibold text-slate-200 uppercase">Notes</span>
          <ul className="mt-1 space-y-1">
            {notes.map((note, i) => (
              <li key={i} className="text-xs text-slate-400">
                • {String(note)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AiPromptDebugDrawer({
  isOpen,
  onClose,
  systemPrompt,
  messages,
  modelId,
  structuredOutput,
  debugInfo,
}: AiPromptDebugDrawerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current && isOpen) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Don't render in production
  if (process.env.NODE_ENV === "production") {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/20 z-40 transition-opacity duration-200",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
      />

      {/* Drawer - full viewport height, 420px wide (fits alongside chat) */}
      <div
        className={cn(
          "fixed top-0 right-0 h-screen w-[420px] max-w-[40vw] bg-slate-900 shadow-2xl z-50",
          "transform transition-transform duration-200 ease-out",
          "flex flex-col",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-white">AI Debug Console</h2>
            <span className="text-xs px-2 py-0.5 rounded bg-orange-600 text-white">
              DEV
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-slate-700 transition-colors"
          >
            <X className="h-4 w-4 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          {/* Model Info */}
          {modelId && (
            <div className="px-4 py-2 bg-slate-800/50 border-b border-slate-700">
              <span className="text-xs text-slate-400">Model: </span>
              <span className="text-xs font-mono text-slate-200">{modelId}</span>
            </div>
          )}

          {/* System Prompt */}
          {systemPrompt && (
            <CollapsibleSection
              title="System Prompt"
              badge={`${systemPrompt.length} chars`}
              badgeColor="bg-purple-600"
            >
              <MessageBlock
                message={{ role: "system", content: systemPrompt }}
                index={-1}
              />
            </CollapsibleSection>
          )}

          {/* Conversation */}
          <CollapsibleSection
            title="Conversation"
            badge={`${messages.length} messages`}
            badgeColor="bg-blue-600"
          >
            {messages.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No messages yet</p>
            ) : (
              messages.map((msg, idx) => (
                <MessageBlock key={msg.id || idx} message={msg} index={idx} />
              ))
            )}
          </CollapsibleSection>

          {/* Structured Output */}
          <CollapsibleSection
            title="Structured Output"
            badge={structuredOutput ? (structuredOutput.status as string) || "parsing" : "waiting"}
            badgeColor={
              structuredOutput?.status === "complete"
                ? "bg-green-600"
                : structuredOutput?.status === "partial"
                  ? "bg-yellow-600"
                  : "bg-slate-600"
            }
          >
            {structuredOutput ? (
              <StructuredOutputSection output={structuredOutput} />
            ) : (
              <p className="text-sm text-slate-500 italic">
                No structured output yet. The AI will output JSON when the conversation concludes.
              </p>
            )}
          </CollapsibleSection>

          {/* Additional Debug Info */}
          {debugInfo && Object.keys(debugInfo).length > 0 && (
            <CollapsibleSection
              title="Debug Info"
              badge={`${Object.keys(debugInfo).length} items`}
              badgeColor="bg-slate-600"
              defaultOpen={false}
            >
              <pre className="text-xs text-slate-300 font-mono whitespace-pre-wrap">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </CollapsibleSection>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-700 bg-slate-800">
          <p className="text-xs text-slate-500">
            Press <kbd className="px-1 py-0.5 rounded bg-slate-700 text-slate-300">Ctrl+Shift+D</kbd> or{" "}
            <kbd className="px-1 py-0.5 rounded bg-slate-700 text-slate-300">Esc</kbd> to close
          </p>
        </div>
      </div>
    </>
  );
}

/**
 * Hook to manage debug drawer state with keyboard shortcut
 */
export function useAiDebugDrawer() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Only enable in development
    if (process.env.NODE_ENV === "production") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+D to toggle
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  };
}
